<template>
  <div class="content py-0">
    <div v-if="loading" class="p-4 text-center text-muted">Loading free item deal...</div>

    <div v-else class="row mx-0 freeItemDeal">
      <!-- Available on / Order type / Status -->
      <div class="col-lg-2 col-md-3 col-sm-12 px-0 panel">
        <div class="panelHeader">Available on</div>
        <div class="panelBody">
          <label v-for="option in availableOnOptions" :key="option" class="checkRow">
            <input type="checkbox" :value="option" v-model="availableOn">
            <span>{{ option }}</span>
          </label>
        </div>

        <div class="panelHeader">Order type</div>
        <div class="panelBody">
          <label v-for="option in orderTypeOptions" :key="option" class="checkRow">
            <input type="checkbox" :value="option" v-model="applicableOrderTypes">
            <span>{{ option }}</span>
          </label>
        </div>

        <div class="panelHeader">Status</div>
        <div class="panelBody">
          <select class="form-control" v-model="status">
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      <!-- Conditions -->
      <div class="col-lg-4 col-md-9 col-sm-12 px-0 panel">
        <div class="panelHeader">Conditions</div>
        <div class="panelBody conditions">
          <div class="formRow">
            <label>Minimum order</label>
            <div class="input-group">
              <div class="input-group-prepend"><span class="input-group-text">£</span></div>
              <input type="number" min="0" step="0.01" class="form-control text-center" v-model="minAmount" placeholder="0.00">
            </div>
          </div>
          <div class="formRow">
            <label>Number of free item</label>
            <select class="form-control text-center" v-model.number="freeItemQty">
              <option v-for="n in 10" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>

          <div class="subTitle mt-4">Date (Optional)</div>
          <div class="formRow">
            <label>Start date</label>
            <input type="date" class="form-control" v-model="startDate">
          </div>
          <div class="formRow">
            <label>End date</label>
            <input type="date" class="form-control" v-model="endDate" :min="startDate">
          </div>
          <div class="hint">Leave blank for no date limit</div>

          <div class="subTitle mt-4">Time (Optional)</div>
          <div class="formRow">
            <label>Start time</label>
            <input type="time" class="form-control" v-model="startTime">
          </div>
          <div class="formRow">
            <label>End time</label>
            <input type="time" class="form-control" v-model="endTime">
          </div>
          <div class="hint">Leave blank for all day. End before start means an overnight window.</div>

          <div class="subTitle mt-4">Days of the week</div>
          <label class="checkRow mb-2">
            <input type="checkbox" :checked="allDaysSelected" @change="toggleAllDays($event.target.checked)">
            <span>All</span>
          </label>
          <div class="days">
            <label v-for="day in weekDays" :key="day" class="day">
              <span>{{ day }}</span>
              <input type="checkbox" :value="day" v-model="days">
            </label>
          </div>

          <div class="text-right mt-4">
            <button type="button" class="btn saveBtn" :disabled="saving" @click="saveDeal">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Select category -->
      <div class="col-lg-6 col-md-12 col-sm-12 px-0 panel">
        <div class="panelHeader">Select category</div>
        <div class="row mx-0">
          <div class="col-6 px-0 listCol">
            <ul class="list-group list-group-flush">
              <li v-for="category in categoryDataList" :key="category.id"
                class="list-group-item listRow"
                :class="{ activeRow: category.id === selectedCategoryId }"
                @click="selectedCategoryId = category.id">
                <span>{{ category.label }}</span>
                <span>
                  <span v-if="selectedCountFor(category.id)" class="badge countBadge">{{ selectedCountFor(category.id) }}</span>
                  <i class="fa fa-chevron-right ml-2"></i>
                </span>
              </li>
            </ul>
          </div>
          <div class="col-6 px-0 listCol">
            <ul class="list-group list-group-flush" v-if="selectedCategoryId">
              <li class="list-group-item listRow">
                <span>All</span>
                <input type="checkbox" :checked="allItemsSelected" :disabled="!itemsOfSelectedCategory.length" @change="toggleAllItems($event.target.checked)">
              </li>
              <li v-for="item in itemsOfSelectedCategory" :key="item.id" class="list-group-item listRow">
                <label class="mb-0 w-100 d-flex justify-content-between align-items-center" style="cursor: pointer;">
                  <span>{{ item.label }}</span>
                  <input type="checkbox" :value="Number(item.id)" v-model="selectedItemIds">
                </label>
              </li>
              <li v-if="!itemsOfSelectedCategory.length" class="list-group-item text-muted">No items in this category</li>
            </ul>
            <div v-else class="p-3 text-muted">Select a category to choose items</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import logout from './../../mixins/logoutMixins.js'

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export default {
  props: {
    categoryDataList: { type: Array, default: () => [] },
    itemDataList: { type: Array, default: () => [] }
  },
  mixins: [logout],
  data () {
    return {
      availableOnOptions: ['EPOS', 'Online'],
      orderTypeOptions: ['Table', 'Collection', 'Waiting', 'Delivery', 'Express'],
      weekDays: WEEK_DAYS,
      loading: false,
      saving: false,
      availableOn: [],
      applicableOrderTypes: [],
      status: 'Inactive',
      minAmount: '',
      freeItemQty: 1,
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      days: [],
      selectedCategoryId: 0,
      selectedItemIds: [],
      // saved items no longer in the menu, kept so saving doesn't drop them
      savedItems: [],
      restaurantId: parseInt(localStorage.getItem('restaurantId')),
      jwtToken: this.$cookies.get('token') || null
    }
  },
  computed: {
    itemsOfSelectedCategory () {
      return this.itemDataList.filter(item => item.categoryId == this.selectedCategoryId)
    },
    allItemsSelected () {
      return this.itemsOfSelectedCategory.length > 0 &&
        this.itemsOfSelectedCategory.every(item => this.selectedItemIds.includes(Number(item.id)))
    },
    allDaysSelected () {
      return this.days.length === this.weekDays.length
    }
  },
  created () {
    this.loadDeal()
  },
  methods: {
    selectedCountFor (categoryId) {
      return this.itemDataList.filter(item => item.categoryId == categoryId && this.selectedItemIds.includes(Number(item.id))).length
    },
    toggleAllItems (checked) {
      const ids = this.itemsOfSelectedCategory.map(item => Number(item.id))
      this.selectedItemIds = this.selectedItemIds.filter(id => !ids.includes(id))
      if (checked) this.selectedItemIds.push(...ids)
    },
    toggleAllDays (checked) {
      this.days = checked ? [...this.weekDays] : []
    },

    // "dd/MM/yyyy" (API) -> "yyyy-MM-dd" (date input)
    toInputDate (value) {
      if (!value) return ''
      const match = value.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/)
      return match ? `${match[3]}-${match[2]}-${match[1]}` : value
    },
    // "yyyy-MM-dd" (date input) -> "dd/MM/yyyy" (API)
    toApiDate (value) {
      if (!value) return ''
      const [year, month, day] = value.split('-')
      return `${day}/${month}/${year}`
    },
    // "05:00 PM" (API) -> "17:00" (time input)
    toInputTime (value) {
      if (!value) return ''
      const match = value.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/)
      if (!match) return ''
      let hours = parseInt(match[1])
      const meridiem = (match[3] || '').toUpperCase()
      if (meridiem === 'PM' && hours < 12) hours += 12
      if (meridiem === 'AM' && hours === 12) hours = 0
      return `${String(hours).padStart(2, '0')}:${match[2]}`
    },
    // "17:00" (time input) -> "05:00 PM" (API)
    toApiTime (value) {
      if (!value) return ''
      const [h, m] = value.split(':')
      const hours = parseInt(h)
      const meridiem = hours >= 12 ? 'PM' : 'AM'
      const hours12 = hours % 12 === 0 ? 12 : hours % 12
      return `${String(hours12).padStart(2, '0')}:${m} ${meridiem}`
    },

    applyDeal (deal) {
      this.status = deal.status || 'Inactive'
      this.availableOn = deal.availableOn || []
      this.applicableOrderTypes = deal.applicableOrderTypes || []
      this.minAmount = deal.minAmount || ''
      this.freeItemQty = parseInt(deal.freeItemQty) || 1
      this.startDate = this.toInputDate(deal.startDate)
      this.endDate = this.toInputDate(deal.endDate)
      this.startTime = this.toInputTime(deal.startTime)
      this.endTime = this.toInputTime(deal.endTime)

      const applicableDays = deal.applicableDays || []
      this.days = applicableDays.includes('All')
        ? [...this.weekDays]
        : this.weekDays.filter(day => applicableDays.includes(day))

      this.savedItems = deal.items || []
      this.selectedItemIds = this.savedItems.map(item => Number(item.itemId))
    },
    requestHeader () {
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.jwtToken}`
      }
    },
    handleAuthError (error) {
      const code = error.response && error.response.status
      if (code === 401 || code === 403) {
        let logoutResponse = this.logout()
        if (logoutResponse === 1) {
          this.$router.push({ path: '/login' })
        }
        return true
      }
      return false
    },
    loadDeal () {
      let url = process.env.VUE_APP_API_URL+`/api/freeItemDeal/v1/public/settings?restaurantId=${this.restaurantId}`

      this.loading = true
      axios
      .get(url)
      .then((response) => {
        this.applyDeal(response.data || {})
      })
      .catch(() => {
        this.$toastr.e('Could not load free item deal', 'Error')
      })
      .finally(() => {
        this.loading = false
      })
    },

    buildItems () {
      return this.selectedItemIds.map(itemId => {
        const menuItem = this.itemDataList.find(item => Number(item.id) === itemId)
        if (!menuItem) {
          return this.savedItems.find(item => Number(item.itemId) === itemId)
        }
        const category = this.categoryDataList.find(cat => cat.id == menuItem.categoryId)
        return {
          categoryId: Number(menuItem.categoryId),
          categoryTitle: category ? category.label : '',
          itemId,
          label: menuItem.label
        }
      }).filter(Boolean)
    },
    validate () {
      if (!this.restaurantId) return 'restaurantId is required'

      const hasAmount = this.minAmount !== '' && this.minAmount !== null
      if (hasAmount && isNaN(parseFloat(this.minAmount))) return 'Minimum order must be a number'
      if (!(this.freeItemQty >= 1 && this.freeItemQty <= 10)) return 'Number of free items must be between 1 and 10'
      if (this.startDate && this.endDate && this.endDate < this.startDate) return 'End date is before start date'
      if (!!this.startTime !== !!this.endTime) return 'Provide both start time and end time'

      if (this.status === 'Active') {
        if (!this.availableOn.length) return 'Select at least one of EPOS or Online'
        if (!this.applicableOrderTypes.length) return 'Select at least one order type'
        if (!this.days.length) return 'Select at least one day of the week'
        if (!hasAmount || parseFloat(this.minAmount) <= 0) return 'Minimum order must be greater than 0'
        if (!this.selectedItemIds.length) return 'Select at least one item'
      }
      return ''
    },
    saveDeal () {
      const error = this.validate()
      if (error) {
        this.$toastr.e(error, 'Error')
        return
      }

      let url = process.env.VUE_APP_API_URL+`/api/freeItemDeal/v1/settings`

      let dataToSubmit = {
        restaurantId: this.restaurantId,
        status: this.status,
        availableOn: this.availableOn,
        applicableOrderTypes: this.applicableOrderTypes,
        minAmount: this.minAmount === '' ? '' : parseFloat(this.minAmount).toFixed(2),
        freeItemQty: this.freeItemQty,
        startDate: this.toApiDate(this.startDate),
        endDate: this.toApiDate(this.endDate),
        startTime: this.toApiTime(this.startTime),
        endTime: this.toApiTime(this.endTime),
        applicableDays: this.allDaysSelected ? ['All'] : this.days,
        items: this.buildItems()
      }

      this.saving = true
      axios
      .post(url, dataToSubmit, { headers: this.requestHeader() })
      .then((response) => {
        const result = response.data || {}
        if (result.status) {
          this.$toastr.s(result.msg || 'Free item deal saved', 'Success')
          if (result.data) this.applyDeal(result.data)
        } else {
          this.$toastr.e(result.error || 'Could not save free item deal', 'Error')
        }
      })
      .catch((err) => {
        if (!this.handleAuthError(err)) {
          this.$toastr.e('Could not save free item deal', 'Error')
        }
      })
      .finally(() => {
        this.saving = false
      })
    }
  }
}
</script>

<style scoped>
.freeItemDeal {
  background: #FFFFFF;
  margin-bottom: 5rem;
}

.panel {
  border-right: 1px solid #F3DCC4;
}

.panelHeader {
  background: #FDEAD5;
  color: #000;
  font-size: 1.3rem;
  font-weight: bold;
  text-align: center;
  padding: 14px 10px;
}

.panelBody {
  padding: 16px 20px;
}

.checkRow {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 1.05rem;
  color: #555;
  margin-bottom: 14px;
}

.checkRow input {
  width: 20px;
  height: 20px;
  margin-right: 16px;
  cursor: pointer;
}

.conditions .formRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.conditions .formRow label {
  margin-bottom: 0;
  flex: 0 0 45%;
  text-align: right;
  padding-right: 14px;
  color: #000;
}

.conditions .formRow .form-control,
.conditions .formRow .input-group {
  flex: 0 0 55%;
  max-width: 55%;
}

.subTitle {
  font-weight: bold;
  text-align: right;
  margin-bottom: 12px;
}

.hint {
  font-size: 0.8rem;
  color: #888;
  text-align: right;
  margin-top: -6px;
}

.days {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.day {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  font-size: 0.9rem;
}

.day input {
  width: 20px;
  height: 20px;
  margin-top: 8px;
  cursor: pointer;
}

.listCol {
  border-right: 1px solid #EEE;
  max-height: calc(100vh - 220px);
  min-height: 300px;
  overflow-y: auto;
}

.listRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 14px 20px;
}

.listRow input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-left: 10px;
  flex-shrink: 0;
  cursor: pointer;
}

.activeRow {
  background: #FFF6EE;
  font-weight: 600;
}

.countBadge {
  background: #FE750F;
  color: #FFF;
}

.saveBtn {
  background: #FFF5FF;
  border: 1px solid #BD31B5;
  color: #FE7611;
  padding: 6px 28px;
}
</style>
